import React from 'react';
import { 
  ArrowLeft,
  Question,
  Info,
  Warning,
  CheckCircle
} from '@phosphor-icons/react';
import { 
  Box,
  Typography,
  Container,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider
} from '@mui/material';
import { PageContainer, Content } from '../../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #000000 0%,
    #1a1a1a 50%,
    #000000 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 6rem 2rem 3rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 24px;
  top: 24px;
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  padding-top: 2rem;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 1rem;
  border-radius: 8px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.5);
  
  &:before {
    display: none;
  }
`;

const FAQ = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What devices are supported?",
      answer: "ServTrackr iOS app is compatible with iPhone and iPad devices running iOS 14.0 or later. This includes iPhone 6s and newer, and iPad 5th generation and newer."
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial for new users. You can access all premium features during the trial period. No credit card is required to start the trial."
    },
    {
      question: "How do I sync data between devices?",
      answer: "Your data automatically syncs across all your devices when you're connected to the internet. You can also manually trigger a sync from the settings menu. All data is securely stored in the cloud."
    },
    {
      question: "Can I use the app offline?",
      answer: "Yes, ServTrackr works offline. Your data will be stored locally and automatically sync when you're back online. However, some features like real-time updates and cloud backup require an internet connection."
    },
    {
      question: "How secure is my data?",
      answer: "We take security seriously. All data is encrypted both in transit and at rest. We use industry-standard security protocols and regular security audits to ensure your data remains protected."
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept all major credit cards, Apple Pay, and PayPal. Payments are processed securely through the App Store's payment system."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel your subscription at any time through the App Store's subscription management. Go to Settings > [Your Name] > Subscriptions, find ServTrackr, and select 'Cancel Subscription'."
    },
    {
      question: "Is there a limit to how many services I can track?",
      answer: "The number of services you can track depends on your subscription plan. The basic plan includes up to 50 services, while premium plans offer unlimited service tracking."
    }
  ];

  return (
    <PageContainer>
      <GradientHero>
        <BackButton onClick={() => navigate('/resources/help-center/ios')}>
          <ArrowLeft size={24} />
        </BackButton>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Question size={64} weight="duotone" style={{ marginBottom: '1rem' }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Find answers to the most common questions about ServTrackr iOS
          </motion.p>
        </HeroContent>
      </GradientHero>

      <Content>
        <Container maxWidth="md">
          <Alert severity="info" sx={{ mb: 4 }}>
            Can't find what you're looking for? Contact our support team for personalized assistance.
          </Alert>

          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <StyledAccordion>
                <AccordionSummary
                  expandIcon={<Info size={24} weight="duotone" />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>
            </motion.div>
          ))}

          <Alert severity="warning" sx={{ mt: 4 }}>
            For the most up-to-date information, please check our website or contact support.
          </Alert>
        </Container>
      </Content>
    </PageContainer>
  );
};

export default FAQ; 